<template>
  <div class="pd-embed-control" :class="{'pd-embed-contorl-disabled':!editable}">
    <div class="flex-wrap" style="padding:0 8px;cursor:pointer;height:100%;" @click="openEditor">
      配置
    </div>

    <Modal
      v-model="modalOpen"
      footer-hide
      :title="modalTitle"
      :width="1760"
      :styles="{top: '2vh'}"
      class-name="perm-acl-modal"
      @on-visible-change="onModalVisible"
    >
      <div class="perm-select-modal">
        <template v-if="!treeData.length && !loading">
          <BaseEmpty :msg="emptyMsg" />
        </template>
        <template v-else>
          <div class="perm-split">
            <!-- 左侧：所见即所得表单预览（默认 1000px） -->
            <div class="perm-preview">
              <div class="perm-panel-head flex-wrap flex-between">
                <span class="perm-panel-title">表单预览</span>
                <span class="perm-panel-hint">
                  随右侧配置实时变化
                  <template v-if="hasClone"> · 克隆对象：{{ cloneObjectName }}</template>
                </span>
              </div>
              <div class="perm-preview-scroll">
                <Spin v-if="loading" fix />
                <UgoForm
                  v-if="modalOpen && !loading && previewForm"
                  :key="previewRenderKey"
                  class="perm-preview-form"
                  :form="previewForm"
                  :value="previewModel"
                  :acl="localAcl"
                  :editable="!visibilityOnly"
                  :as_view="visibilityOnly"
                />
                <BaseEmpty v-else-if="!loading" msg="表单未加载" />
              </div>
            </div>

            <!-- 右侧：配置项（列居中对齐） -->
            <div
              class="perm-config"
              :class="{
                'perm-config--bind': isFlowMode && !visibilityOnly,
                'perm-config--vis-only': visibilityOnly,
              }"
            >
              <div class="perm-panel-head flex-wrap flex-between">
                <span class="perm-panel-title">权限配置</span>
                <Button size="small" @click="clearAllConfig">清空</Button>
              </div>

              <div class="perm-cfg-head">
                <div class="perm-cfg-name">字段</div>
                <div class="perm-cfg-check">可见</div>
                <div v-if="!visibilityOnly" class="perm-cfg-check">可编辑</div>
                <div v-if="isFlowMode && !visibilityOnly" class="perm-cfg-bind">默认值</div>
              </div>

              <div class="perm-cfg-scroll">
                <Spin v-if="loading" fix />
                <div
                  v-for="row in flatConfigRows"
                  :key="row.node.fullkey"
                  class="perm-cfg-row"
                  :class="{
                    'perm-cfg-row--active': activeKey === row.node.fullkey,
                    'perm-cfg-row--group': row.node._isGroup,
                    'perm-cfg-row--root': row.node._isRoot,
                    'perm-cfg-row--hidden': rowIsHiddenLeaf(row.node),
                  }"
                  @click="activeKey = row.node.fullkey"
                >
                  <div
                    class="perm-cfg-name"
                    :style="{paddingLeft: (8 + row.depth * 14) + 'px'}"
                    :title="row.node.name + ' · ' + rowControlLabel(row.node)"
                  >
                    <Icon
                      class="perm-cfg-icon"
                      :custom="`ugoicon ugi${rowControlIcon(row.node)}`"
                      :size="15"
                    />
                    <span class="perm-cfg-label">{{ row.node.name }}</span>
                  </div>

                  <div class="perm-cfg-check">
                    <Checkbox
                      v-if="rowHasKeys(row.node)"
                      :value="rowVisState(row.node).checked"
                      :indeterminate="rowVisState(row.node).indeterminate"
                      @click.native.stop
                      @input="onToggleVisible(row.node, $event)"
                    />
                  </div>

                  <div v-if="!visibilityOnly" class="perm-cfg-check">
                    <Checkbox
                      v-if="rowHasKeys(row.node)"
                      :value="rowEditState(row.node).checked"
                      :indeterminate="rowEditState(row.node).indeterminate"
                      :disabled="rowEditDisabled(row.node)"
                      @click.native.stop
                      @input="onToggleEditable(row.node, $event)"
                    />
                  </div>

                  <div v-if="isFlowMode && !visibilityOnly" class="perm-cfg-bind">
                    <Select
                      v-if="rowCanBind(row.node)"
                      :value="localBinds[row.node.fullkey] || ''"
                      size="small"
                      transfer
                      clearable
                      placeholder="无"
                      @click.native.stop
                      @input="onBindChange(row.node.fullkey, $event)"
                    >
                      <Option
                        v-for="opt in bindOptions"
                        :key="opt.value || 'none'"
                        :value="opt.value"
                      >{{ opt.label }}</Option>
                    </Select>
                  </div>
                </div>
                <BaseEmpty v-if="!loading && !flatConfigRows.length" msg="无可配置字段" />
              </div>
            </div>
          </div>

          <div class="perm-footer flex-wrap flex-right">
            <Button type="primary" style="margin-right:10px;" @click="handleSave">确定</Button>
            <Button @click="handleCancel">取消</Button>
          </div>
        </template>
      </div>
    </Modal>
  </div>
</template>

<script>
import Vue from 'vue';
import cloneDeep from 'lodash/cloneDeep';
import UgoForm from '@/components/form/index';
import {loadRemoteFormDef} from '@/components/form/remote-form-def-cache';
import {
  BIND_CLONE_VALUE,
  BIND_EXECUTOR,
  BIND_CREATED_AT,
  BIND_OPINION,
  BIND_AGREE,
  unpackAclMap,
  unpackBindsMap,
  packAclValue,
  getBindOptions,
  setByPath,
  isOpinionBindable,
} from '@/components/flow/flow-acl';

const LAYOUT_CONTROLS = new Set([
  'UgoRow', 'UgoSection', 'UgoTabs', 'UgoCol', 'UgoTab',
  'Row', 'Section', 'Tabs', 'Col', 'Tab',
]);

const NESTED_CONTROLS = new Set([
  'UgoInnerForm', 'UgoMultiForm',
  'InnerForm', 'MultiForm',
]);

const TABLE_CONTROLS = new Set([
  'UgoTable', 'Table',
  'UgoZzlTable', 'ZzlTable', 'ZZLTable',
]);

const SKIP_TABLE_COL = new Set(['drag', 'index']);

const ICON_FALLBACK = {
  Form: 'form',
  Col: 'biaoge',
  Field: 'input',
  UgoInput: 'input',
  Input: 'input',
  UgoTextarea: 'changwenbenxiangsi',
  Textarea: 'changwenbenxiangsi',
  UgoSection: 'form',
  Section: 'form',
  UgoRow: 'biaoge',
  Row: 'biaoge',
  UgoTable: 'biaoge',
  Table: 'biaoge',
  UgoInnerForm: 'biaoge',
  InnerForm: 'biaoge',
  UgoMultiForm: 'biaoge',
  MultiForm: 'biaoge',
};

function normalizeControl(control) {
  return String(control || '');
}

function isLayout(control) {
  return LAYOUT_CONTROLS.has(normalizeControl(control));
}

function isNested(control) {
  return NESTED_CONTROLS.has(normalizeControl(control));
}

function isTable(control) {
  return TABLE_CONTROLS.has(normalizeControl(control));
}

function isRemoteNestedControl(control) {
  return isNested(control);
}

function lookupComponentIcon(control) {
  const raw = normalizeControl(control);
  if (!raw || raw === 'Form') return ICON_FALLBACK.Form;
  const names = [raw];
  if (raw.startsWith('Ugo')) names.push(raw.slice(3));
  else names.push(`Ugo${raw}`);
  if (/^Zzl/i.test(raw)) {
    names.push(raw.replace(/^Zzl/i, 'Ugo'));
  }
  for (let i = 0; i < names.length; i++) {
    const comp = Vue.options.components[names[i]];
    if (!comp) continue;
    const icon = (comp.options && comp.options.icon) || comp.icon;
    if (icon) return icon;
  }
  return ICON_FALLBACK[raw] || ICON_FALLBACK.Field;
}

export default {
  components: {UgoForm},
  data() {
    return {
      modalOpen: false,
      loading: false,
      localAcl: {},
      localBinds: {},
      formDef: null,
      treeData: [],
      previewModel: {},
      activeKey: '',
    };
  },
  props: ['value', 'editable', 'option', 'db'],
  computed: {
    isFlowMode() {
      return this.option?.mode === 'form';
    },
    /** 抄送等：仅配置可见性 */
    visibilityOnly() {
      return !!this.option?.visibilityOnly;
    },
    modalTitle() {
      return this.visibilityOnly ? '表单可见性配置' : '表单权限配置';
    },
    emptyMsg() {
      return this.isFlowMode ? '请先绑定视图' : '请先绑定数据模型';
    },
    formKey() {
      if (!this.isFlowMode) {
        return this.db && this.db.model;
      }
      if (this.option?.form_id) {
        return this.option.form_id;
      }
      return this.db && this.db.form;
    },
    hasClone() {
      const cb = this.db && this.db.clonedBy;
      if (!cb) return false;
      if (typeof cb === 'string') return !!cb;
      return !!(cb.key);
    },
    hasOpinion() {
      return isOpinionBindable(this.db || {});
    },
    /** 克隆对象可读名，如「部门」 */
    cloneObjectName() {
      const cb = this.db && this.db.clonedBy;
      if (!cb) return this.db?.clonedType || '对象';
      if (typeof cb === 'string') {
        return this.db?.clonedType || cb;
      }
      return (cb.control && cb.control.name) ||
        cb.name ||
        this.db?.clonedType ||
        cb.key ||
        '对象';
    },
    bindOptions() {
      if (this.visibilityOnly) return [];
      return getBindOptions({
        hasClone: this.hasClone,
        cloneName: this.cloneObjectName,
        hasOpinion: this.hasOpinion,
      });
    },
    previewRenderKey() {
      // 仅随表单切换重挂载；ACL/绑定靠 props 响应，避免频繁销毁导致空白
      return `perm-preview-${(this.formDef && (this.formDef.id || this.formDef.name)) || 'form'}`;
    },
    /** 预览表单：补全 fullkey + 注入内嵌表 ACL；宽度随左侧面板，默认最大 1000 */
    previewForm() {
      if (!this.formDef) return null;
      const form = cloneDeep(this.formDef);
      if (!form.id) {
        form.id = form._id || this.formKey || 'preview-form';
      }
      const cfg = {...(form.config || {})};
      const w = Number(cfg.width);
      if (!(w > 0) || Number.isNaN(w)) {
        cfg.width = 1000;
      }
      form.config = cfg;
      this.ensureFieldFullkeys(form.fields || [], '');
      this.injectNestedAcl(form.fields || [], this.localAcl, '');
      return form;
    },
    flatConfigRows() {
      const rows = [];
      const walk = (nodes, depth) => {
        (nodes || []).forEach((node) => {
          rows.push({node, depth});
          if (node.fields && node.fields.length) {
            walk(node.fields, depth + 1);
          }
        });
      };
      walk(this.treeData, 0);
      return rows;
    },
  },
  watch: {
    formKey: {
      handler() {
        this.loadForm();
      },
      immediate: true,
    },
    localBinds: {
      handler() {
        this.syncPreviewModel();
      },
      deep: true,
    },
  },
  methods: {
    openEditor() {
      if (!this.editable) return;
      this.localAcl = unpackAclMap(this.value);
      this.localBinds = this.visibilityOnly ? {} : unpackBindsMap(this.value);
      this.activeKey = '';
      this.modalOpen = true;
      this.loadForm().then(() => {
        this.syncPreviewModel();
      });
    },
    onModalVisible(visible) {
      if (!visible) {
        this.localAcl = {};
        this.localBinds = {};
        this.previewModel = {};
        this.activeKey = '';
      }
    },
    /**
     * 为表单字段写入与 ACL 树一致的 fullkey，保证左侧预览 acl[fullkey] 能命中
     */
    ensureFieldFullkeys(fields, parentPath = '') {
      (fields || []).forEach((field, index) => {
        if (!field) return;
        const control = normalizeControl(field.control);
        const key = field.key || field.name;
        const layout = isLayout(control);
        const skipPath = layout;
        const selfPath = (!key || skipPath) ?
          parentPath :
          (parentPath ? `${parentPath}.${key}` : String(key));
        if (key && !skipPath) {
          field.fullkey = selfPath;
        } else {
          field.fullkey = field.id ?
            String(field.id) :
            `${parentPath || 'root'}__${control || 'node'}__${index}`;
        }
        const childPath = skipPath ? parentPath : selfPath;
        if (Array.isArray(field.fields) && field.fields.length) {
          this.ensureFieldFullkeys(field.fields, childPath);
        }
        // InnerForm 远程字段：预览时 InnerForm 自载模型，相对 key 注入见 injectNestedAcl
        if (Array.isArray(field._remoteFields) && field._remoteFields.length) {
          this.ensureFieldFullkeys(field._remoteFields, childPath);
        }
      });
    },
    /**
     * 把 parent.child 形式的 ACL 拆进 InnerForm/MultiForm.config.acl（相对路径）
     * 这样左侧内嵌表也能按右侧配置隐藏/只读
     */
    injectNestedAcl(fields, acl, parentPath = '') {
      const map = acl || {};
      (fields || []).forEach((field) => {
        if (!field) return;
        const control = normalizeControl(field.control);
        const key = field.key || field.name;
        const layout = isLayout(control);
        const nested = isNested(control);
        const skipPath = layout;
        const selfPath = (!key || skipPath) ?
          parentPath :
          (parentPath ? `${parentPath}.${key}` : String(key));
        const childPath = skipPath ? parentPath : selfPath;

        if (nested && key) {
          const prefix = selfPath;
          const nestedAcl = {};
          Object.keys(map).forEach((k) => {
            if (k === prefix) return;
            if (k.indexOf(prefix + '.') === 0) {
              nestedAcl[k.slice(prefix.length + 1)] = map[k];
            }
          });
          field.config = {...(field.config || {}), acl: nestedAcl};
        }
        if (Array.isArray(field.fields) && field.fields.length) {
          this.injectNestedAcl(field.fields, map, childPath);
        }
      });
    },
    syncPreviewModel() {
      const model = {};
      Object.keys(this.localBinds || {}).forEach((fullkey) => {
        const bind = this.localBinds[fullkey];
        if (!bind) return;
        let val;
        if (bind === BIND_EXECUTOR) {
          const u = this.$store?.state?.session?.user;
          val = (u && (u.id != null ? u.id : u.name)) || '（操作人）';
        } else if (bind === BIND_CREATED_AT) {
          const d = new Date();
          const pad = (n) => String(n).padStart(2, '0');
          val = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
        } else if (bind === BIND_CLONE_VALUE) {
          val = this.resolveCloneDemoValue();
        } else if (bind === BIND_OPINION) {
          val = '（审批意见）';
        } else if (bind === BIND_AGREE) {
          val = '同意';
        } else {
          return;
        }
        setByPath(model, fullkey, val);
      });
      this.previewModel = model;
    },
    resolveCloneDemoValue() {
      const name = this.cloneObjectName || '对象';
      const cb = this.db && this.db.clonedBy;
      const control = (cb && typeof cb === 'object' && cb.control && cb.control.control) || '';
      const clonedType = (this.db && this.db.clonedType) || '';
      if (clonedType === '部门' || /DepSelect/i.test(control)) {
        return {id: '__clone_preview__', name: `（克隆-${name}）`};
      }
      if (clonedType === '成员' || clonedType === '员工' || /UserSelect/i.test(control)) {
        return {id: '__clone_preview__', name: `（克隆-${name}）`};
      }
      // 选项类：尽量用克隆字段 option 的第一项
      const options = cb && typeof cb === 'object' && cb.control && cb.control.option &&
        cb.control.option.options;
      if (Array.isArray(options) && options.length) {
        const first = options[0];
        if (first != null && typeof first === 'object') {
          return first.key != null ? first.key : (first.name || `（克隆-${name}）`);
        }
        return first;
      }
      return `（克隆-${name}）`;
    },
    async loadForm() {
      if (!this.formKey) {
        this.formDef = null;
        this.treeData = [];
        return;
      }
      this.loading = true;
      try {
        let form = null;
        if (this.isFlowMode) {
          const res = await this.$api.get(`forms/${this.formKey}`);
          form = cloneDeep((res.data && res.data.data) || {});
        } else {
          await this.$store.dispatch('forms/get', this.formKey);
          const fromStore = this.$store.getters['forms/form'];
          form = fromStore ? cloneDeep(fromStore) : null;
        }
        if (form && Array.isArray(form.fields)) {
          await this.resolveNestedForms(form.fields);
          this.ensureFieldFullkeys(form.fields, '');
          this.formDef = form;
          this.treeData = [{
            id: form.id || 'form-root',
            fullkey: form.id || 'form-root',
            name: form.name || '表单',
            control: 'Form',
            _isRoot: true,
            _isGroup: true,
            fields: this.buildAclTree(form.fields, ''),
          }];
          this.syncPreviewModel();
        } else {
          this.formDef = null;
          this.treeData = [];
        }
      } catch (e) {
        this.formDef = null;
        this.treeData = [];
      } finally {
        this.loading = false;
      }
    },
    async resolveNestedForms(fields, ancestors = new Set()) {
      const list = fields || [];
      for (const field of list) {
        if (Array.isArray(field.fields) && field.fields.length) {
          await this.resolveNestedForms(field.fields, ancestors);
        }
        if (!isRemoteNestedControl(field.control)) continue;
        const modelId = field.config && field.config.model;
        if (!modelId) {
          field._remoteFields = [];
          field._remoteError = '未配置 model';
          continue;
        }
        if (ancestors.has(modelId)) {
          field._remoteFields = [];
          field._remoteError = '循环引用';
          continue;
        }
        try {
          const def = await loadRemoteFormDef({
            store: this.$store,
            api: this.$api,
            modelId,
            view: 'editor',
          });
          const remoteFields = cloneDeep((def && def.fields) || []);
          field._remoteFields = remoteFields;
          field._remoteModelName = (def && (def.name || def.title)) || modelId;
          delete field._remoteError;
          const nextAncestors = new Set(ancestors);
          nextAncestors.add(modelId);
          await this.resolveNestedForms(remoteFields, nextAncestors);
        } catch (e) {
          field._remoteFields = [];
          field._remoteError = (e && e.message) ? e.message : '加载失败';
        }
      }
    },
    buildAclTree(fields, parentPath = '') {
      const nodes = [];
      (fields || []).forEach((field, index) => {
        if (!field) return;
        const control = normalizeControl(field.control);
        const key = field.key || field.name;
        const layout = isLayout(control);
        const nested = isNested(control);
        const table = isTable(control);

        const inlineChildren = Array.isArray(field.fields) ? field.fields : [];
        const remoteChildren = Array.isArray(field._remoteFields) ? field._remoteFields : [];
        const childSource = nested && remoteChildren.length ?
          remoteChildren :
          (inlineChildren.length ? inlineChildren : remoteChildren);

        const skipPath = layout;
        const selfPath = (!key || skipPath) ?
          parentPath :
          (parentPath ? `${parentPath}.${key}` : String(key));
        const childPath = skipPath ? parentPath : selfPath;

        let children = [];
        if (table) {
          children = this.buildTableColNodes(field, selfPath);
        } else if (childSource.length) {
          children = this.buildAclTree(childSource, childPath);
        }

        let displayName = field.name || key || control || '未命名';
        if (nested && field.config && field.config.model) {
          displayName = `${displayName} → ${field.config.model}`;
        }
        if (field._remoteError) {
          displayName = `${displayName} (${field._remoteError})`;
        }

        const hasOwnKey = !!(key && !skipPath);
        const isGroup = layout || nested || table || children.length > 0;
    // 与 ensureFieldFullkeys 保持一致；布局节点也要固定 fullkey（通常为 id）
        const treeKey = hasOwnKey ?
          (field.fullkey || selfPath || key) :
          (field.fullkey || (field.id ? String(field.id) :
            `${parentPath || 'root'}__${control || 'node'}__${index}`));

        field.fullkey = treeKey;

        nodes.push({
          id: treeKey,
          ...field,
          fullkey: treeKey,
          name: displayName,
          fields: children,
          _isGroup: isGroup,
          _hasOwnKey: hasOwnKey,
          _selectable: hasOwnKey || children.length > 0,
        });
      });
      return nodes;
    },
    buildTableColNodes(field, tablePath) {
      const cols = (field.config && field.config.cols) || [];
      return cols
        .filter((col) => col && !SKIP_TABLE_COL.has(normalizeControl(col.control)))
        .map((col, i) => {
          const colKey = col.key || col.name || `col${i}`;
          const fullkey = tablePath ? `${tablePath}.${colKey}` : String(colKey);
          return {
            id: fullkey,
            ...col,
            key: colKey,
            fullkey,
            name: col.name || colKey,
            control: col.control || 'Input',
            fields: [],
            _isGroup: false,
            _hasOwnKey: true,
            _selectable: true,
            _isTableCol: true,
          };
        });
    },
    collectKeys(node, out = []) {
      if (!node) return out;
      // 布局组（栏目等）也写入自身 fullkey，便于运行态整块隐藏
      if (node.fullkey && !node._isRoot && (node._hasOwnKey || node._isGroup)) {
        if (!out.includes(node.fullkey)) out.push(node.fullkey);
      }
      (node.fields || []).forEach((c) => this.collectKeys(c, out));
      return out;
    },
    allKeys() {
      const keys = [];
      (this.treeData || []).forEach((n) => this.collectKeys(n, keys));
      return keys;
    },
    aclOf(fullkey) {
      const v = this.localAcl[fullkey];
      return v == null ? 0 : Number(v) || 0;
    },
    isVisible(fullkey) {
      return this.aclOf(fullkey) < 2;
    },
    isEditable(fullkey) {
      return this.aclOf(fullkey) < 1;
    },
    subtreeState(node, kind) {
      const keys = this.collectKeys(node);
      if (!keys.length) return {checked: false, indeterminate: false};
      let yes = 0;
      keys.forEach((k) => {
        if (kind === 'visible' ? this.isVisible(k) : this.isEditable(k)) yes += 1;
      });
      return {
        checked: yes === keys.length,
        indeterminate: yes > 0 && yes < keys.length,
      };
    },
    rowHasKeys(node) {
      return this.collectKeys(node).length > 0;
    },
    rowIsGroup(node) {
      return !!(node._isGroup && node.fields && node.fields.length);
    },
    rowIsHiddenLeaf(node) {
      return !!(node._hasOwnKey && !this.rowIsGroup(node) && !this.isVisible(node.fullkey));
    },
    rowVisState(node) {
      if (this.rowIsGroup(node) || node._isRoot) {
        return this.subtreeState(node, 'visible');
      }
      if (node._hasOwnKey) {
        return {checked: this.isVisible(node.fullkey), indeterminate: false};
      }
      return {checked: false, indeterminate: false};
    },
    rowEditState(node) {
      if (this.rowIsGroup(node) || node._isRoot) {
        return this.subtreeState(node, 'editable');
      }
      if (node._hasOwnKey) {
        return {checked: this.isEditable(node.fullkey), indeterminate: false};
      }
      return {checked: false, indeterminate: false};
    },
    rowEditDisabled(node) {
      if (this.rowIsGroup(node) || node._isRoot) return false;
      return !this.rowVisState(node).checked;
    },
    /** 不可见字段仍可配置默认值（执行时写入 formData） */
    rowCanBind(node) {
      if (this.visibilityOnly) return false;
      return !!(this.isFlowMode && node._hasOwnKey && !this.rowIsGroup(node) && !node._isRoot);
    },
    rowControlLabel(node) {
      if (node._isRoot) return 'Form';
      if (node._isTableCol) return 'Col';
      return node.control || 'Field';
    },
    rowControlIcon(node) {
      if (node._isRoot) return ICON_FALLBACK.Form;
      if (node._isTableCol) return lookupComponentIcon(node.control || 'Input');
      return lookupComponentIcon(node.control);
    },
    setKeysVisible(keys, visible) {
      keys.forEach((k) => {
        if (!visible) {
          this.$set(this.localAcl, k, 2);
          return;
        }
        // 抄送仅可见：显示为只读(1)；审批节点保留原可编辑状态
        if (this.visibilityOnly) {
          this.$set(this.localAcl, k, 1);
          return;
        }
        const cur = this.aclOf(k);
        this.$set(this.localAcl, k, cur === 1 ? 1 : 0);
      });
    },
    setKeysEditable(keys, editable) {
      keys.forEach((k) => {
        if (!this.isVisible(k) && !editable) {
          this.$set(this.localAcl, k, 2);
          return;
        }
        this.$set(this.localAcl, k, editable ? 0 : 1);
      });
    },
    onToggleVisible(node, checked) {
      this.setKeysVisible(this.collectKeys(node), checked);
    },
    onToggleEditable(node, checked) {
      this.setKeysEditable(this.collectKeys(node), checked);
    },
    clearAllConfig() {
      this.localAcl = {};
      this.localBinds = {};
      this.syncPreviewModel();
    },
    onBindChange(fullkey, val) {
      if (!val) {
        this.$delete(this.localBinds, fullkey);
      } else {
        this.$set(this.localBinds, fullkey, val);
      }
    },
    handleSave() {
      if (this.visibilityOnly) {
        // 抄送：只保留可见性（隐藏=2，可见=只读1），不写 binds
        const flat = {};
        Object.keys(this.localAcl || {}).forEach((k) => {
          const n = Number(this.localAcl[k]);
          if (n === 2) flat[k] = 2;
          else if (n === 1 || n === 0) flat[k] = 1;
        });
        this.$emit('input', this.isFlowMode ? packAclValue(flat, {}) : flat);
      } else if (this.isFlowMode) {
        this.$emit('input', packAclValue(this.localAcl, this.localBinds));
      } else {
        const flat = {};
        Object.keys(this.localAcl).forEach((k) => {
          const n = Number(this.localAcl[k]);
          if (!Number.isNaN(n) && n > 0) flat[k] = n;
        });
        this.$emit('input', flat);
      }
      this.modalOpen = false;
    },
    handleCancel() {
      this.modalOpen = false;
    },
  },
};
</script>

<style lang="less">
.perm-acl-modal {
  .ivu-modal {
    max-width: 98vw;
    width: 98vw !important;
  }
  .ivu-modal-body {
    padding: 12px 16px 14px;
    max-height: calc(96vh - 56px);
    overflow: hidden;
  }
}

.perm-select-modal {
  height: calc(94vh - 110px);
  min-height: 520px;
  display: flex;
  flex-direction: column;

  .perm-split {
    flex: 1;
    min-height: 0;
    display: flex;
    gap: 12px;
  }

  .perm-panel-head {
    flex-shrink: 0;
    gap: 8px;
    margin-bottom: 8px;
    min-height: 28px;
    align-items: center;
    padding: 0 2px;
  }
  .perm-panel-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--text2, #333);
  }
  .perm-panel-hint {
    font-size: 12px;
    color: var(--subtext2);
  }

  .perm-preview {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    border: 1px solid var(--border);
    border-radius: 6px;
    background: #f7f8fa;
    padding: 10px 10px 0;
  }
  .perm-preview-scroll {
    flex: 1;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    position: relative;
    background: #fff;
    border: 1px solid var(--border);
    border-radius: 4px 4px 0 0;
    padding: 12px 14px 20px;
  }
  .perm-preview-form.ugo-form-root,
  .perm-preview-scroll .ugo-form-root {
    /* 随左侧面板宽度，最大 1000，避免左右滚动条 */
    width: 100% !important;
    max-width: 1000px !important;
    background: #fff !important;
  }

  .perm-config {
    width: 400px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    border: 1px solid var(--border);
    border-radius: 6px;
    background: #fff;
    padding: 10px 0 0;
    --perm-check-w: 56px;
    --perm-bind-w: 0px;
    --perm-cfg-pad: 8px;
  }
  .perm-config--bind {
    width: 480px;
    --perm-bind-w: 120px;
  }
  .perm-config--vis-only {
    width: 340px;
  }

  .perm-cfg-head,
  .perm-cfg-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) var(--perm-check-w) var(--perm-check-w) var(--perm-bind-w);
    column-gap: 0;
    align-items: center;
    padding: 0 var(--perm-cfg-pad);
    box-sizing: border-box;
  }
  .perm-config:not(.perm-config--bind):not(.perm-config--vis-only) {
    .perm-cfg-head,
    .perm-cfg-row {
      grid-template-columns: minmax(0, 1fr) var(--perm-check-w) var(--perm-check-w);
    }
  }
  .perm-config--vis-only {
    .perm-cfg-head,
    .perm-cfg-row {
      grid-template-columns: minmax(0, 1fr) var(--perm-check-w);
    }
  }

  .perm-cfg-head {
    flex-shrink: 0;
    height: 32px;
    border-bottom: 1px solid var(--border);
    font-size: 12px;
    font-weight: 600;
    color: var(--subtext2);
  }
  .perm-cfg-head .perm-cfg-check,
  .perm-cfg-head .perm-cfg-bind {
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
  }

  .perm-cfg-scroll {
    flex: 1;
    min-height: 0;
    overflow: auto;
    position: relative;
  }

  .perm-cfg-row {
    min-height: 36px;
    border-bottom: 1px dashed var(--border);
    cursor: pointer;
    transition: background 0.12s ease;
    &:hover {
      background: rgba(45, 140, 240, 0.04);
    }
    &.perm-cfg-row--active {
      background: rgba(45, 140, 240, 0.1);
    }
    &.perm-cfg-row--group .perm-cfg-label {
      font-weight: 500;
    }
    &.perm-cfg-row--hidden .perm-cfg-label {
      color: var(--subtext2);
    }
  }

  .perm-cfg-name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 12px;
    line-height: 1.4;
    display: flex;
    align-items: center;
  }
  .perm-cfg-icon {
    flex-shrink: 0;
    margin-right: 6px;
    color: var(--subtext2);
    vertical-align: middle;
  }
  .perm-cfg-label {
    color: var(--primary);
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .perm-cfg-check {
    width: var(--perm-check-w);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .perm-cfg-bind {
    width: var(--perm-bind-w);
    display: flex;
    align-items: center;
    justify-content: center;
    .ivu-select {
      width: 110px;
    }
  }

  .perm-footer {
    flex-shrink: 0;
    margin-top: 14px;
    padding-top: 2px;
  }
}
</style>
